import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import {
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { employmentApi } from '../../../services/api';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';

const EventTypesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [showControls, setShowControls] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Unified modal state
  const [eventTypeModal, setEventTypeModal] = useState({
    open: false,
    isEdit: false,
    formData: { code: '', description: '' },
    originalId: null
  });

  const [deleteModal, setDeleteModal] = useState({ open: false, eventType: null });

  const fetchEventTypes = async () => {
    try {
      setIsLoading(true);
      const data = await employmentApi.getEventTypes();
      setEventTypes(data);
    } catch (err) {
      showAlert('Failed to load event types.', 'error', err);
      console.error('Error fetching event types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEventTypes();
  }, []);

  // Unified handler for both add and edit
  const handleSaveEventType = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      if (eventTypeModal.isEdit) {
        // Edit existing event type
        if (!eventTypeModal.originalId) {
          showAlert('Invalid event type data for update', 'error');
          return;
        }

        await employmentApi.updateEventType(eventTypeModal.originalId, {
          code: eventTypeModal.formData.code,
          description: eventTypeModal.formData.description || null
        });
        showAlert('Event type updated successfully', 'success');
      } else {
        // Add new event type
        await employmentApi.createEventType({
          code: eventTypeModal.formData.code,
          description: eventTypeModal.formData.description || null
        });
        showAlert('Event type added successfully', 'success');
      }

      await fetchEventTypes();
      // Reset modal state
      setEventTypeModal({
        open: false,
        isEdit: false,
        formData: { code: '', description: '' },
        originalId: null
      });
    } catch (err) {
      const action = eventTypeModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} event type.`, 'error', err);
      console.error(`Error ${action}ing event type:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEventType = (eventType) => {
    setDeleteModal({ open: true, eventType });
  };

  const confirmDelete = async () => {
    const typeToDelete = deleteModal.eventType;
    setDeleteModal({ open: false, eventType: null });

    if (!typeToDelete?.id) {
      showAlert('Invalid event type data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await employmentApi.deleteEventType(typeToDelete.id);
      await fetchEventTypes();
      showAlert('Event type deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting event type:', err);
      showAlert(err.message || 'Failed to delete event type.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEventTypes = eventTypes.filter(type => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    return (type.code && type.code.toLowerCase().includes(lowerSearch)) ||
           (type.description && type.description.toLowerCase().includes(lowerSearch));
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Event Types Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Event Type"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            {ability.can(ACTIONS.CREATE, SUBJECTS.EVENT_TYPE) && (
              <HeaderButton onClick={() => setEventTypeModal({
                open: true,
                isEdit: false,
                formData: { code: '', description: '' },
                originalId: null
              })}>
                <FaPlus /> Add Event Type
              </HeaderButton>
            )}
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <div style={{
                color: '#64748B',
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredEventTypes.length}
                </span>
                {filteredEventTypes.length === 1 ? 'event type' : 'event types'} found
              </div>
              <Grid
                container
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredEventTypes.map((eventType) => (
                  <Grid item key={eventType.id}>
                    <Card
                      sx={{
                        width: '100%',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '4px',
                              fontFamily: 'monospace',
                              fontSize: '16px'
                            }}
                          >
                            {eventType.code}
                          </Typography>
                          {eventType.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6c757d',
                                marginBottom: '8px'
                              }}
                            >
                              {eventType.description}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          {ability.can(ACTIONS.UPDATE, SUBJECTS.EVENT_TYPE) && (
                            <Button
                              onClick={() => setEventTypeModal({
                                open: true,
                                isEdit: true,
                                formData: {
                                  code: eventType.code || '',
                                  description: eventType.description || ''
                                },
                                originalId: eventType.id
                              })}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#0d6efd',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                  color: '#0d6efd'
                                }
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {ability.can(ACTIONS.DELETE, SUBJECTS.EVENT_TYPE) && (
                            <Button
                              onClick={() => handleDeleteEventType(eventType)}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#dc3545',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                  color: '#dc3545'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing event types */}
      <Modal
        isOpen={eventTypeModal.open}
        onClose={() => setEventTypeModal({
          open: false,
          isEdit: false,
          formData: { code: '', description: '' },
          originalId: null
        })}
        onSubmit={handleSaveEventType}
        title={eventTypeModal.isEdit ? "Edit Event Type" : "Add Event Type"}
        submitText={eventTypeModal.isEdit ? "Update" : "Add"}
      >
        <Box mb={2}>
          <TextField
            id="event-type-code"
            name="code"
            label="Code"
            required
            value={eventTypeModal.formData.code}
            onChange={(e) => setEventTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter event type code"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="event-type-description"
            name="description"
            label="Description"
            value={eventTypeModal.formData.description}
            onChange={(e) => setEventTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, description: e.target.value }
            }))}
            placeholder="Enter description (optional)"
            autoComplete="off"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
          />
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, eventType: null })}
        onConfirm={confirmDelete}
        title="Delete Event Type"
        message={`Are you sure you want to delete the event type "${deleteModal.eventType?.code}"? This action cannot be undone.`}
      />
    </Container>
  );
};

export default EventTypesConfiguration;